import { getFromLocalStorage, setToLocalStorage } from '../../common/dom-helpers'
import { getJifUser, getPrincipal } from '../identity'
import type { ContextApi, Pbx, PbxInfoParams, User } from './models'

/**
 * The GTC experience is currently the only one that tracks the user's selected PBX using a local storage
 * key/value. However some other experiences (Contact Center) will need to know the PBX id when the Shell
 * bootstraps, independently of any other experience loading.
 *
 * This is done by first checking whether a pbxId is saved in local storage from GTC.
 * If no value from GTC is available, we check the ShellLocalContext value in local storage.
 * If we find either of the above values in local storage, we perform an API call to check that the value is in the approved list from the Jif Api
 *
 * If neither of the above is available, we take the first PBX id from the list returned by the Jif API.
 */

export let userAccountsWithPBx: any

//TODO: add error handling for this function to avoid stalling out Shell start
//https://jira.ops.expertcity.com/browse/SCORE-1685
export const getPbxId = async (email: string, externalUserKey: string): Promise<string | undefined> => {
  const gtcSelectedDeviceLocalStorageKey = formatGTCSelectedDeviceLocalStorageKey(externalUserKey)
  const gtcSelectedDevicePbxId = getPbxIdFromGTCSelectedDevice(gtcSelectedDeviceLocalStorageKey)
  /**
   * Two API calls are necessary, as the Jif API returns most of the data needed, however,
   * the state ('ACTIVE' vs 'DEACTIVATED') is only available from the Principal API
   */
  const [pbxListFromPrincipalApi, pbxListFromJifApi] = await Promise.all([
    getAccountsWithPbxFromPrincipalApi(email),
    getPbxListFromJifApi(email),
  ])

  const pbxIdListFromJifApi = pbxListFromJifApi.map(pbx => pbx.id)
  const localStoragePbxIdIsValid = (pbxId: string) =>
    pbxIdListFromJifApi.includes(pbxId) && isValidPbx(pbxId, pbxListFromPrincipalApi, pbxListFromJifApi)

  if (gtcSelectedDevicePbxId && localStoragePbxIdIsValid(gtcSelectedDevicePbxId)) {
    return gtcSelectedDevicePbxId
  }

  const shellPbxId = getPbxIdFromShellLocalStorage(externalUserKey)

  if (shellPbxId && localStoragePbxIdIsValid(shellPbxId)) {
    return shellPbxId
  }

  return pbxIdListFromJifApi.find(pbxId => isValidPbx(pbxId, pbxListFromPrincipalApi, pbxListFromJifApi))
}

export const getPbxUserId = async (email: string, pbxId: string) => {
  const pbxListFromJifApi = await getPbxListFromJifApi(email)
  return pbxListFromJifApi.find(pbx => pbx.id === pbxId)?.userInfo?.userId
}

export const formatGTCSelectedDeviceLocalStorageKey = (externalUserKey: string) =>
  `jiveweb_userSelectedDevice_${externalUserKey}`

export const formatShellContextLocalStorageKey = (id: string) => `goto-context_${id}`

export const getPbxIdFromGTCSelectedDevice = (key: string): string | undefined => {
  const gtcSelectedDeviceLocalStorageValue = getFromLocalStorage(key)

  if (gtcSelectedDeviceLocalStorageValue) {
    const gtcSelectedDevice = JSON.parse(gtcSelectedDeviceLocalStorageValue)
    return gtcSelectedDevice.pbx.id
  }
}

export const getPbxListFromJifApi = async (email: string): Promise<readonly Pbx[]> => {
  const jifUserData = await getJifUser(email)
  /**
   * When the API call is made for a user without g2c entitlement, it returns a 404 with an object.
   * For the moment, it made sense to set all properties below to optional in order to default to []
   * in case of a user without a pbx. Open to changing to a try/catch in the future?
   */

  return jifUserData?.data?.tenants?.pbxes ?? []
}

export const getAccountsWithPbxFromPrincipalApi = async (email: string): Promise<readonly User[]> => {
  const { users } = await getPrincipal(email)
  userAccountsWithPBx = users ?? []
  return users ?? []
}

export const pbxIsActive = (pbxList: readonly User[], pbxId: string) =>
  pbxList.find(user => user.jrn.includes(pbxId))?.state === 'ACTIVE' ?? false

const pbxHasExtensions = (pbxList: readonly Pbx[], pbxId: string) =>
  !!pbxList.find(pbx => pbx.id === pbxId)?.extensions?.length

export const isValidPbx = (pbxId: string, principalPbxList: readonly User[], jifPbxList: readonly Pbx[]) =>
  pbxIsActive(principalPbxList, pbxId) && pbxHasExtensions(jifPbxList, pbxId)

const getContextFromShellLocalStorage = (externalUserKey: string): ContextApi | undefined => {
  const shellContextLocalStorageKey = formatShellContextLocalStorageKey(externalUserKey)
  const shellContextLocalStorageValue = getFromLocalStorage(shellContextLocalStorageKey)
  let shellContext
  try {
    shellContext = JSON.parse(shellContextLocalStorageValue ?? '')
    if (!shellContext || typeof shellContext !== 'object') {
      shellContext = {}
    }
  } catch {
    shellContext = {}
  }
  return shellContext
}

export const getAccountKeyFromShellLocalStorage = (externalUserKey: string): string | undefined => {
  const shellContextLocalStorage = getContextFromShellLocalStorage(externalUserKey)
  return shellContextLocalStorage?.account?.key
}

const getPbxIdFromShellLocalStorage = (externalUserKey: string): string | undefined => {
  const shellContextLocalStorage = getContextFromShellLocalStorage(externalUserKey)
  return shellContextLocalStorage?.pbx?.id
}

export const setContextLocalStorageAccountKey = (externalUserKey: string, accountKey: string) => {
  const shellContextLocalStorageKey = formatShellContextLocalStorageKey(externalUserKey)
  const shellContextLocalStorage = getContextFromShellLocalStorage(externalUserKey)

  setToLocalStorage(
    shellContextLocalStorageKey,
    JSON.stringify({ ...shellContextLocalStorage, account: { key: accountKey } }),
  )
}

export const setContextLocalStoragePbxInfo = (pbxInfoParams: PbxInfoParams) => {
  const shellContextLocalStorageKey = formatShellContextLocalStorageKey(pbxInfoParams.externalUserKey)
  const shellContextLocalStorage = getContextFromShellLocalStorage(pbxInfoParams.externalUserKey)

  setToLocalStorage(
    shellContextLocalStorageKey,
    JSON.stringify({ ...shellContextLocalStorage, pbx: { id: pbxInfoParams.pbxId, userId: pbxInfoParams.pbxUserId } }),
  )
}

export const setContextLocalStorageCurrentMountedApps = (externalUserKey: string, currentApps: readonly string[]) => {
  const shellContextLocalStorageKey = formatShellContextLocalStorageKey(externalUserKey)
  const shellContextLocalStorage = getContextFromShellLocalStorage(externalUserKey)

  setToLocalStorage(shellContextLocalStorageKey, JSON.stringify({ ...shellContextLocalStorage, currentApps }))
}

export const getContextLocalStorageCurrentMountedApps = (externalUserKey: string): readonly string[] => {
  const shellContextLocalStorage = getContextFromShellLocalStorage(externalUserKey)
  return shellContextLocalStorage?.currentApps ?? []
}
